import { useMemo } from 'react';
import {
  startOfDay,
  endOfDay,
  isSameDay,
  isBefore,
  isAfter,
  addDays,
  format,
} from 'date-fns';
import { it } from 'date-fns/locale';

import { useDatabase } from '@/context/DatabaseContext';
import { useAuth } from '@/context/AuthContext';
import { useSpaces } from './useSpaces';
import { isCompletedStatus } from '@/constants/taskStatuses';
import type { Task } from '@/types';

export interface MyDaySection {
  id: string;
  label: string;
  tasks: Array<Task & { id: string }>;
}

export interface MyDayData {
  greeting: string;
  dateLabel: string;
  userName: string;
  sections: {
    focus: MyDaySection;
    dueToday: MyDaySection;
    overdue: MyDaySection;
    upcoming: MyDaySection;
  };
  totalScheduledToday: number;
  totalTimeEstimate: number;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Buongiorno';
  if (hour < 18) return 'Buon pomeriggio';
  return 'Buonasera';
}

export function useMyDay(): MyDayData {
  const { tasks: allTasks } = useDatabase();
  const { user } = useAuth();
  const { containersMap } = useSpaces();

  const now = useMemo(() => new Date(), []);
  const todayStart = useMemo(() => startOfDay(now), [now]);
  const todayEnd = useMemo(() => endOfDay(now), [now]);
  const weekEnd = useMemo(() => endOfDay(addDays(now, 7)), [now]);

  // Filter tasks belonging to current org's spaces
  const orgTasks = useMemo(() => {
    if (!containersMap || containersMap.size === 0) return allTasks;
    return allTasks.filter(t => containersMap.has(t.spaceId));
  }, [allTasks, containersMap]);

  // Filter tasks assigned to current user
  const userTasks = useMemo(() => {
    const userId = user?.id;
    if (!userId) return orgTasks;
    return orgTasks.filter(t =>
      t.assignees && t.assignees.includes(userId)
    );
  }, [orgTasks, user]);

  const sections = useMemo(() => {
    const focusTasks: Array<Task & { id: string }> = [];
    const dueTodayTasks: Array<Task & { id: string }> = [];
    const overdueTasks: Array<Task & { id: string }> = [];
    const upcomingTasks: Array<Task & { id: string }> = [];

    // Track IDs already in focus to avoid duplicates in dueToday
    const focusIds = new Set<string>();

    for (const task of userTasks) {
      const isCompleted = isCompletedStatus(task.status);

      // Focus: tasks with startDate today (or dueDate today if no startDate), not completed
      const taskStart = task.startDate ?? task.dueDate;
      if (
        taskStart &&
        isSameDay(new Date(taskStart), now) &&
        !isCompleted
      ) {
        focusTasks.push(task);
        focusIds.add(task.id);
        continue;
      }

      // Due today (not already in focus, not completed)
      if (
        task.dueDate &&
        isSameDay(new Date(task.dueDate), now) &&
        !focusIds.has(task.id) &&
        !isCompleted
      ) {
        dueTodayTasks.push(task);
        continue;
      }

      // Overdue: dueDate before today, not completed
      if (
        task.dueDate &&
        isBefore(new Date(task.dueDate), todayStart) &&
        !isCompleted
      ) {
        overdueTasks.push(task);
        continue;
      }

      // Upcoming: dueDate in next 7 days (after today, not completed)
      if (
        task.dueDate &&
        isAfter(new Date(task.dueDate), todayEnd) &&
        isBefore(new Date(task.dueDate), weekEnd) &&
        !isCompleted
      ) {
        upcomingTasks.push(task);
      }
    }

    // Sort focus tasks by startDate
    focusTasks.sort((a, b) => (a.startDate || a.dueDate || 0) - (b.startDate || b.dueDate || 0));

    // Sort overdue tasks by dueDate (most overdue first)
    overdueTasks.sort((a, b) => (a.dueDate || 0) - (b.dueDate || 0));

    // Sort upcoming tasks by dueDate
    upcomingTasks.sort((a, b) => (a.dueDate || 0) - (b.dueDate || 0));

    return {
      focus: {
        id: 'focus',
        label: 'Focus',
        tasks: focusTasks,
      },
      dueToday: {
        id: 'due-today',
        label: 'In scadenza',
        tasks: dueTodayTasks,
      },
      overdue: {
        id: 'overdue',
        label: 'Scaduti',
        tasks: overdueTasks,
      },
      upcoming: {
        id: 'upcoming',
        label: 'Prossimi',
        tasks: upcomingTasks,
      },
    };
  }, [userTasks, now, todayStart, todayEnd, weekEnd]);

  // Stats
  const totalScheduledToday = sections.focus.tasks.length + sections.dueToday.tasks.length;

  const totalTimeEstimate = useMemo(() => {
    const allSectionTasks = [
      ...sections.focus.tasks,
      ...sections.dueToday.tasks,
    ];
    return allSectionTasks.reduce((acc, task) => acc + (task.timeEstimated || 0), 0);
  }, [sections.focus.tasks, sections.dueToday.tasks]);

  // User name from auth metadata
  const userName = useMemo(() => {
    if (!user) return '';
    const meta = user.user_metadata;
    if (meta?.full_name) return meta.full_name as string;
    if (meta?.name) return meta.name as string;
    if (user.email) return user.email.split('@')[0];
    return '';
  }, [user]);

  const greeting = getGreeting();
  const dateLabel = format(now, "EEEE d MMMM yyyy", { locale: it });

  return {
    greeting,
    dateLabel,
    userName,
    sections,
    totalScheduledToday,
    totalTimeEstimate,
  };
}
